<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';

  import { PluginsService } from '@client/services/plugins';

  import { appStore } from '@client/stores/app';

  import { GeneralDeleteForm } from '../index.js';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const getIntegrations = async () => {
    const integrations = await PluginsService.getAll();
    return integrations.map((integration) => ({
      label: integration.rulename,
      value: integration.rulename,
      ...integration
    }));
  };

  const onDeleteIntegration = async (integration) => {
    const deleteResponse = await PluginsService.deletePlugin(integration);

    if (deleteResponse?.status >= 400) {
      notify($format('label.FAILURE.DELETE', { values: { type: integration.rulename } }), 'error');
      throw new Error($format('label.FAILURE.DELETE_INTEGRATION'));
    }

    notify($format('label.SUCCESS.DELETE', { values: { type: integration.rulename } }));

    step.data = {
      id: integration.rulename,
      integrationData: {
        defaulttext: integration.defaulttext,
        params: integration.params,
        provider: integration.provider,
        rulename: integration.rulename,
        status: integration.status
      }
    };
  };

  const rollbackDeleteIntegration = async () => {
    if (!step.data?.integrationData) {
      return; // Nothing to rollback
    }

    $appStore.loading = true;

    try {
      const response = await PluginsService.savePlugin(step.data.integrationData);

      if (response?.status >= 400) {
        throw new Error($format('label.FAILURE.ROLLBACK_INTEGRATION_DELETION'));
      }
    } catch (error) {
      console.error('Failed to rollback integration deletion:', error);
    } finally {
      $appStore.loading = false;
    }
  };
</script>

<GeneralDeleteForm
  bind:nextDisabled
  bind:step
  {onExecute}
  {onRollback}
  rollbackFunction={rollbackDeleteIntegration}
  getFunction={getIntegrations}
  deleteFunction={onDeleteIntegration}
  showPreview={true}
>
  <div slot="preview" let:item>
    {#if item}
      <div class="integration-preview">
        <div class="provider-logo">
          <img
            src="data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="
            style="background: url('https://assets.opensense.com/plugins/{item.provider ||
              'opensense'}.svg') no-repeat center center; background-size: contain;"
            alt={item.provider}
            draggable="false"
          />
        </div>
        <div class="integration-details">
          <div class="detail-item">
            <span class="label">{$format('label.NAME')}:</span>
            <span class="value integration-name">{item.rulename}</span>
          </div>
          {#if item.params && item.params.length > 0}
            <div class="detail-item">
              <span class="label">{$format('label.PARAMETERS')}:</span>
              <span class="value">{item.params.length} {$format('label.CONFIGURED')}</span>
            </div>
          {/if}
          {#if item.defaulttext}
            <div class="detail-item">
              <span class="label">{$format('label.FALLBACK_TEXT')}:</span>
              <span class="value">{item.defaulttext}</span>
            </div>
          {/if}
          <div class="detail-item">
            <span class="label">{$format('label.STATUS')}:</span>
            <span class="value {item.status ? 'status-active' : 'status-inactive'}">
              {item.status ? $format('label.ACTIVE') : $format('label.INACTIVE')}
            </span>
          </div>
        </div>
      </div>
    {/if}
  </div>
</GeneralDeleteForm>

<style lang="scss">
  .integration-preview {
    align-items: center;
    background-color: var(--color-white);
    display: flex;
    flex-direction: column;
    padding: var(--padding-base);

    .provider-logo {
      align-items: center;
      display: flex;
      height: 60px;
      justify-content: center;
      margin-bottom: var(--margin-base);
      width: 100%;

      img {
        background-size: contain !important;
        display: block;
        height: 40px;
        pointer-events: none;
        touch-action: none;
        user-select: none;
        width: 100px;
      }
    }

    .integration-details {
      display: flex;
      flex-direction: column;
      gap: var(--margin-small);
      width: 100%;

      .detail-item {
        display: flex;
        gap: var(--margin-small);

        .label {
          color: var(--color-gray-600);
          flex-shrink: 0;
          font-weight: var(--font-weight-medium);
        }

        .value {
          color: var(--color-gray-900);

          &.integration-name {
            font-weight: var(--font-weight-medium);
          }

          &.status-active {
            color: var(--color-green-600);
            font-weight: var(--font-weight-medium);
          }

          &.status-inactive {
            color: var(--color-gray-500);
          }
        }
      }
    }
  }
</style>
